import React, { useState } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import "tailwindcss/tailwind.css";
import AddTaskModal from "./AddTaskModal";
import axiosInstance from "../api/Axios"; // Импорт экземпляра axios
import TaskCard from "./TaskCard"; // Импорт компонента карточки задачи
import axios from "axios"; // Для получения забавного текста

// Функция для перестановки карточек внутри одной категории
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list); // Клонирование массива для иммутабельности
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

// Функция для перемещения карточек между категориями
const move = (sourceTasks, destinationTasks, source, destination) => {
  const sourceClone = Array.from(sourceTasks);
  const destClone = Array.from(destinationTasks);
  const [removed] = sourceClone.splice(source.index, 1);

  // При перемещении обновляем только позиционные данные (в объекте removed задаём новую droppableId)
  removed.category = destination.droppableId;
  destClone.splice(destination.index, 0, removed);

  return {
    [source.droppableId]: sourceClone,
    [destination.droppableId]: destClone,
  };
};

const TaskBoard = ({
  tasks,
  setTasks,
  developers,
  projects,
  categories,
  setSelectedTask,
  fetchTasks,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("🤡 Loading...");
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    developer: "none",
    project: "all",
    hours: 0,
  });

  // Функция для получения забавного текста для экрана загрузки
  const fetchFunnyText = async () => {
    try {
      const response = await axios.get(
        "https://official-joke-api.appspot.com/random_joke"
      );
      const joke = `${response.data.setup} 😂 ${response.data.punchline}`;
      setLoadingText(joke);
    } catch (error) {
      setLoadingText("🤡 Failed to load joke, please try again later.");
    }
  };

  // Функция для обновления позиций карточек в отдельной коллекции
  const updatePositions = async (newState) => {
    const positions = [];
    Object.keys(newState).forEach((categoryId) => {
      newState[categoryId].forEach((task, index) => {
        positions.push({
          taskId: task.id,
          droppableId: categoryId,
          position: index,
        });
      });
    });
    try {
      await axiosInstance.post("/updatepositions", positions);
    } catch (error) {
      console.error("Ошибка обновления позиций:", error);
    }
  };

  // Обработка события drag and drop
  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;

    let completedTask = null;
    let newState = tasks;

    try {
      const sameList = source.droppableId === destination.droppableId;

      // Обновляем задачи (либо reorder, либо move)
      if (sameList) {
        newState = {
          ...tasks,
          [source.droppableId]: reorder(
            tasks[source.droppableId],
            source.index,
            destination.index
          ),
        };
      } else {
        const movedResult = move(
          tasks[source.droppableId],
          tasks[destination.droppableId],
          source,
          destination
        );

        newState = { ...tasks, ...movedResult };

        // Отметим завершённую задачу
        if (destination.droppableId === "items3") {
          completedTask =
            movedResult[destination.droppableId][destination.index];
          completedTask.completedAt = new Date().toISOString();
        }
      }

      setTasks(newState);
      await updatePositions(newState);
    } catch (error) {
      console.error("Error during drag and drop operation:", error);
    } finally {
      if (completedTask) {
        try {
          await sendTaskToTelegram(completedTask._id);
        } catch (err) {
          console.error("Failed to send task to Telegram:", err);
        }
      }

      try {
        await fetchTasks();
      } catch (err) {
        console.error("Failed to fetch tasks:", err);
      }
    }
  };

  // Функция для отправки задачи в Telegram
  const sendTaskToTelegram = async (taskId) => {
    try {
      await axiosInstance.post(`/sendtask/${taskId}`);
    } catch (error) {
      console.error("Error sending task to Telegram:", error);
    }
  };

  // Обработчики изменения разработчика, проекта и часов
  const handleDeveloperChange = async (taskId, developerId) => {
    const newState = { ...tasks };
    for (const key in newState) {
      newState[key] = newState[key].map((task) =>
        task.id === taskId ? { ...task, developer: developerId } : task
      );
    }
    try {
      await axiosInstance.put(`/edittask/${taskId}`, {
        developer: developerId,
      });
      setTasks(newState);
    } catch (error) {
      console.error("Error updating developer:", error);
    }
    await fetchTasks();
    setSelectedTask(null);
  };

  const handleProjectChange = async (taskId, projectId) => {
    const newState = { ...tasks };
    for (const key in newState) {
      newState[key] = newState[key].map((task) =>
        task.id === taskId ? { ...task, project: projectId } : task
      );
    }
    try {
      await axiosInstance.put(`/edittask/${taskId}`, { project: projectId });
      setTasks(newState);
    } catch (error) {
      console.error("Error updating project:", error);
    }
    await fetchTasks();
  };

  const handleHoursChange = async (taskId, hours) => {
    const newState = { ...tasks };
    for (const key in newState) {
      newState[key] = newState[key].map((task) =>
        task.id === taskId ? { ...task, hours: hours } : task
      );
    }
    try {
      await axiosInstance.put(`/edittask/${taskId}`, { hours });
      setTasks(newState);
    } catch (error) {
      console.error("Error updating hours:", error);
    }
    await fetchTasks();
  };

  // Обработчик архивирования задачи
  const handleArchiveTask = async (taskId) => {
    try {
      await axiosInstance.post(`/movetask/${taskId}`);
      await fetchTasks();
    } catch (error) {
      console.error("Error archiving task:", error);
    }
  };

  // Открытие модального окна редактирования
  const openEditModal = (task) => {
    setTaskToEdit(task);
    setNewTask(task);
    setIsModalOpen(true);
  };

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="loader" style={{ color: "#f0f0f0" }}>
            {loadingText}
          </div>
        </div>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        {/* Контейнер с тремя колонками для категорий */}
        <div className="grid grid-cols-3 h-full">
          {categories.map((category) => (
            <Droppable key={category.id} droppableId={category.id}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="p-4 h-screen overflow-y-auto relative border border-gray-300"
                >
                  <div className="w-full bg-white shadow-md rounded-md mb-4 text-center py-2">
                    <h2 className="text-lg font-bold">{category.title}</h2>
                  </div>
                  <div className="space-y-2">
                    {tasks[category.id] && tasks[category.id].length > 0 ? (
                      tasks[category.id]
                        .filter((task) => task.display)
                        .map((item, index) => (
                          <TaskCard
                            key={item.id}
                            item={item}
                            index={index}
                            developers={developers}
                            projects={projects}
                            handleDeveloperChange={handleDeveloperChange}
                            handleProjectChange={handleProjectChange}
                            handleHoursChange={handleHoursChange}
                            handleArchiveTask={handleArchiveTask}
                            openEditModal={openEditModal}
                          />
                        ))
                    ) : (
                      <div className="text-gray-500 text-center">Нет задач</div>
                    )}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
        {isModalOpen && (
          <AddTaskModal
            developers={developers}
            projects={projects}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            newTask={newTask}
            setNewTask={setNewTask}
            setTasks={setTasks}
            taskToEdit={taskToEdit}
            fetchTasks={fetchTasks}
          />
        )}
      </DragDropContext>
    </div>
  );
};

export default TaskBoard;
